import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { formatId } from '@/utils/strings';
import { ROUTES } from '@/router/routes';
import { useGetClinics } from '@/hooks/api/use-get-clinics';
import { Clinic, eliminateClinic } from '@/api/clinics';
import { ALERT_CONFIG, swalert } from '@/libs/sweetalert';

import { BasicDrawer as Drawer } from '@/components/ui/basic-drawer';
import { FetchStates } from '@/components/fetch-states';
import { Table } from '@/components/ui/table';
import { Section } from '@/components/content-layout/section';
import { SectionHeader } from '@/components/content-layout/section-header';
import { Button } from '@/components/ui/button';

import { ClinicSitesTable } from './clinic-sites-table';

export function ClinicsAdminPage() {
  const { clinicId, siteId } = useParams();
  const path = useLocation();

  const isSiteRoute = path.pathname.includes('site');

  // if clinicId is present, open the drawer
  const [isOpen, setIsOpen] = useState(() => Boolean(clinicId));
  const { t } = useTranslation();
  const { clinics, loading, refetchClinics } = useGetClinics();
  const navigate = useNavigate();

  const sitesFormTitle = siteId
    ? `${t('site-drawer-title-edit')} ${formatId(siteId)}`
    : t('site-drawer-title-add');

  const clinicFormTitle = clinicId
    ? `${t('clinic-drawer-title-edit')} ${formatId(clinicId)}`
    : t('clinic-drawer-title-add');

  const drawerTitle = isSiteRoute ? sitesFormTitle : clinicFormTitle;

  const clinicsTableHeaders = useMemo(
    () => [
      {
        key: 'clinic_id',
        label: t('clinic-id'),
        type: 'value' as const,
        format: formatId,
      },
      { key: 'clinic_name', label: t('clinic-name') },
      { key: 'description', label: t('description') },
      {
        key: 'country',
        label: t('country'),
      },
      {
        key: 'city',
        label: t('city'),
      },
      {
        key: 'sites',
        type: 'collapsible' as const,
        label: t('sites'),
      },
      {
        key: 'actions',
        type: 'actions' as const,
        edit: (clinic: Clinic) => {
          setIsOpen((open) => !open);
          navigate(`${ROUTES.ADMIN_EDIT_CLINIC}/${clinic.clinic_id}`);
        },
        delete: async (clinic: Clinic) => {
          await onEliminateClinic(clinic);
        },
      },
    ],
    [t, navigate]
  );

  const onClose = () => {
    setIsOpen(false);
    navigate(-1);
  };

  const onFormClose = async () => {
    onClose();
    await refetchClinics();
  };

  const onEliminateClinic = async (clinic: Clinic) => {
    const result = await swalert.fire({
      ...ALERT_CONFIG,
      title: t('eliminate-clinic-title'),
      html: `<p style="margin-bottom: 1rem;">${t('eliminate-clinic-description').replace('{clinic_name}', `<span style="font-weight: bold">${clinic.clinic_name ?? ''}</span>`)}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('yes'),
      cancelButtonText: t('no'),
    });

    if (result.isConfirmed) {
      await eliminateClinic({ clinic_id: clinic.clinic_id });
      await refetchClinics();
    }
  };

  return (
    <>
      <Drawer title={drawerTitle} open={isOpen || Boolean(clinicId)} onClose={onClose}>
        <Outlet
          context={
            { onFormClose } satisfies {
              onFormClose: () => Promise<void>;
            }
          }
        />
      </Drawer>

      <Section>
        <SectionHeader title={t('clinics-title')} element="h1">
          <Button onClick={() => setIsOpen((open) => !open)} asChild>
            <Link to="new">
              <Plus className="h-5 w-5" />
              {t('new-clinic-button')}
            </Link>
          </Button>
        </SectionHeader>

        <FetchStates loading={loading} refetch={refetchClinics} empty={!clinics?.length}>
          <div className="page-content">
            <Table
              headers={clinicsTableHeaders}
              data={clinics ?? []}
              CollapsibleComponent={ClinicSitesTable}
            />
          </div>
        </FetchStates>
      </Section>
    </>
  );
}
